import React, { useState, useRef, useEffect, useCallback } from "react";
import { GrantsLabelItem } from "../../../services/grantsApi";
import { getColorConfig } from "./labelColors";

interface LabelCellProps {
  articleId: string;
  labels: GrantsLabelItem[];
  currentLabel?: { labelId: string; labelName: string; color: string } | null;
  onSetLabel: (articleId: string, labelId: string | null) => Promise<any>;
}

const LabelCell: React.FC<LabelCellProps> = ({
  articleId,
  labels,
  currentLabel,
  onSetLabel,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);

  /** Build the selectable options list: labels + optional "remove" action */
  const getSelectableOptions = useCallback(() => {
    const options: { id: string | null; label: string }[] = labels.map((l) => ({
      id: l.id,
      label: l.name,
    }));
    if (currentLabel) {
      options.push({ id: null, label: "Usuń etykietę" });
    }
    return options;
  }, [labels, currentLabel]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  /** Reset focused index when dropdown opens/closes */
  useEffect(() => {
    if (isOpen) {
      setFocusedIndex(-1);
    }
  }, [isOpen]);

  /** Scroll focused option into view */
  useEffect(() => {
    if (focusedIndex >= 0 && optionRefs.current[focusedIndex]) {
      optionRefs.current[focusedIndex]?.focus();
    }
  }, [focusedIndex]);

  const handleSelect = async (labelId: string | null) => {
    setSaving(true);
    try {
      await onSetLabel(articleId, labelId);
    } catch (err) {
      // Error handled by parent
    } finally {
      setSaving(false);
      setIsOpen(false);
    }
  };

  const handleDropdownKeyDown = (e: React.KeyboardEvent) => {
    const options = getSelectableOptions();
    if (options.length === 0) {
      if (e.key === "Escape" || e.key === "Tab") {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown": {
        e.preventDefault();
        setFocusedIndex((prev) =>
          prev < options.length - 1 ? prev + 1 : 0
        );
        break;
      }
      case "ArrowUp": {
        e.preventDefault();
        setFocusedIndex((prev) =>
          prev > 0 ? prev - 1 : options.length - 1
        );
        break;
      }
      case "Enter": {
        e.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < options.length) {
          handleSelect(options[focusedIndex].id);
        }
        break;
      }
      case "Escape": {
        e.preventDefault();
        setIsOpen(false);
        triggerRef.current?.focus();
        break;
      }
      case "Tab": {
        setIsOpen(false);
        break;
      }
    }
  };

  const handleTriggerKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
      if (!isOpen) {
        e.preventDefault();
        setIsOpen(true);
      }
    }
  };

  const scrollToLabelsPanel = () => {
    const panel = document.querySelector('[data-testid="label-management-panel"]');
    if (panel) {
      panel.scrollIntoView({ behavior: "smooth", block: "start" });
      const panelButton = panel.querySelector("button");
      if (panelButton) {
        panelButton.click();
        setTimeout(() => {
          const addButton = panel.querySelector('[data-testid="add-label-button"]');
          if (addButton instanceof HTMLElement) {
            addButton.click();
          }
        }, 100);
      }
    }
  };

  if (saving) {
    return (
      <span className="text-xs text-neutral-400 animate-pulse">...</span>
    );
  }

  const colorCfg = currentLabel ? getColorConfig(currentLabel.color) : null;

  return (
    <div ref={ref} className="relative">
      <button
        ref={triggerRef}
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={handleTriggerKeyDown}
        className="flex items-center gap-1 text-sm hover:bg-neutral-100 rounded px-1 py-0.5 cursor-pointer max-w-[120px]"
        title={currentLabel ? currentLabel.labelName : "Przypisz etykietę"}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        {currentLabel && colorCfg ? (
          <>
            <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${colorCfg.dot}`} />
            <span className="truncate text-xs">{currentLabel.labelName}</span>
          </>
        ) : (
          <span className="text-neutral-400 text-xs">&mdash;</span>
        )}
      </button>

      {isOpen && (
        <div
          className="absolute z-50 mt-1 w-44 bg-white border border-neutral-200 rounded-md shadow-lg py-1 left-0"
          role="listbox"
          aria-label="Wybierz etykietę"
          onKeyDown={handleDropdownKeyDown}
        >
          {labels.length === 0 ? (
            <div className="px-3 py-2 text-xs text-neutral-500">
              <p className="mb-2">Nie masz jeszcze etykiet.</p>
              <button
                onClick={scrollToLabelsPanel}
                className="text-primary-600 hover:text-primary-800 font-medium underline focus:outline-none focus:ring-2 focus:ring-primary-500 rounded"
              >
                Utwórz pierwszą etykietę
              </button>
            </div>
          ) : (
            <>
              {labels.map((label, index) => {
                const cfg = getColorConfig(label.color);
                const isSelected = currentLabel?.labelId === label.id;
                return (
                  <button
                    key={label.id}
                    ref={(el) => { optionRefs.current[index] = el; }}
                    onClick={() => handleSelect(label.id)}
                    role="option"
                    aria-selected={isSelected}
                    className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 hover:bg-neutral-50 focus:bg-neutral-100 focus:outline-none ${
                      isSelected ? "bg-neutral-100 font-medium" : ""
                    }`}
                  >
                    <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${cfg.dot}`} />
                    <span className="truncate">{label.name}</span>
                  </button>
                );
              })}
              {currentLabel && (
                <>
                  <hr className="my-1 border-neutral-200" />
                  <button
                    ref={(el) => { optionRefs.current[labels.length] = el; }}
                    onClick={() => handleSelect(null)}
                    role="option"
                    aria-selected={false}
                    className="w-full text-left px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 focus:bg-red-100 focus:outline-none"
                  >
                    Usuń etykietę
                  </button>
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default LabelCell;
